/**
 * @Author: Nemo
 * @Description: 动态表单组件 - 支持配置驱动的表单渲染和校验
 * @Date: 2024-05-14
 * @FilePath: /rhcn-checkout-web/src/componentsV2/busi/dynamic-form/index.vue
 */
<template>
  <div class="dynamic-form">
    <!-- 遍历需要显示的表单字段 -->
    <div
      class="form-field-wrapper"
      v-for="field in renderFields"
      :key="field.name"
      :id="`field-${field.name}`"
    >
      <!-- 字段标签区域 -->
      <div class="form-field-header" v-if="!field.isRowFields">
        <!-- 字段标签 -->
        <div class="field-label" v-show="showLabel(field)">
          <span class="label-text">{{ getLabel(field) }}</span>
        </div>

        <!-- 必填错误信息 - 右上角 -->
        <transition name="slide-top" appear>
          <div
            v-if="(isFullValidation && field.require === 'Y' && !formData[field.name] && !field.isRowFields) ||
                 (field.name === 'cardNum' && cardInfoRequiredError)"
            class="field-error top-right"
          >
            {{ $t('message.validator.required') }}
          </div>
        </transition>
      </div>

      <!-- 表单控件 -->
      <div :class="{'highlight-error': errors[field.name]}">
        <!-- 行字段组 -->
        <template v-if="field.isRowFields && field.rowFields && field.rowFields.length">
          <FormRowFields
            :fields="field.rowFields"
            :value="formData"
            :errors="errors"
            :field="field"
            @field-input="onRowFieldInput"
            @field-focus="onFieldFocus"
            @field-blur="onFieldBlur"
            @field-change="onFieldChange"
            @field-validate="onFieldValidate"
          />
        </template>
        <!-- 普通字段 -->
        <template v-else>
          <FormField
            :field="getFieldConfig(field)"
            :component-type="getComponentType(field)"
            v-model="formData[field.name]"
            :error="!!errors[field.name]"
            @focus="onFieldFocus"
            @blur="onFieldBlur"
            @change="onFieldChange"
            @validate="onFieldValidate"
            :data-field="field.name"
            :ref="`field-${field.name}`"
            :extra-props="extraProps"
          />
        </template>
      </div>

      <!-- 格式错误信息 - 左下角 (非必填错误) -->
      <transition name="slide-bottom" appear>
        <div
          v-if="errors[field.name] && (formData[field.name] || field.require !== 'Y') && !(field.componentType === 'card-information' || field.renderComponent === 'card-information' || field.renderComponent === 'CardInformationForm' || field.isRowFields)"
          class="field-error bottom-left"
        >
          {{ errors[field.name] }}
        </div>
      </transition>
    </div>

    <!-- 表单操作区域，可选 -->
    <div v-if="showSubmitButton" class="dynamic-form__actions">
      <OnButton
        @click="handleSubmit"
        :loading="submitting"
        :disabled="submitting"
      >
        {{ submitButtonText }}
      </OnButton>
    </div>
  </div>
</template>

<script>
import OnButton from '@/componentsV2/base/button/index.vue';
import { createValidator } from '@/plugins/validator/index';
import STATES_OPTIONS from '@/utils/tools/state.js';

import FormField from './FormField.vue';
import FormRowFields from './FormRowFields.vue';

export default {
  name: 'DynamicForm',
  components: {
    FormField,
    FormRowFields,
    OnButton,
  },

  props: {
    // 表单字段配置数组，由后端返回并通过field-config处理后的配置
    config: {
      type: Array,
      required: true,
      default: () => [],
    },

    // 是否显示提交按钮
    showSubmitButton: {
      type: Boolean,
      default: false,
    },

    // 初始值，可选
    initialValues: {
      type: Object,
      default: () => ({}),
    },

    // 提交按钮文本
    submitButtonText: {
      type: String,
      default: '提交',
    },

    // 验证模式：format-only(仅格式验证) 或 full(包含必填验证)
    validationMode: {
      type: String,
      default: 'format-only',
      validator: value => ['format-only', 'full'].includes(value),
    },

    // 额外属性，传递给指定类型的子组件
    extraProps: {
      type: Object,
      default: () => ({}),
    },

    // 注：此字段已废弃，保留属性是为了向后兼容
    getInstallmentOptions: {
      type: Function,
      default: null,
    },

    // 注：此字段已废弃，保留属性是为了向后兼容
    canInstallment: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // 表单数据对象
      formData: {},

      // 错误信息对象
      errors: {},

      // 国家为【US/CA/CN】时为select，其它为input
      provinceSelectCountries: ['US', 'CA', 'CN'],

      // 州/省选项
      stateOptions: {},

      // 字段验证器
      validators: {},

      // 提交状态
      submitting: false,

      // 内部使用的验证模式状态
      internalValidationMode: this.validationMode,

      // 卡信息组件必填错误标记
      cardInfoRequiredError: false,
    };
  },

  computed: {
    // 过滤出需要渲染的字段
    renderFields() {
      // 先获取所有需要显示的字段
      const fieldsToRender = this.config.filter(field => field.display === 'Y');

      // 特殊处理卡信息组件：如果存在使用CardInformationForm的cardNum字段，跳过effectiveDate和cvv字段
      const hasCardInfoComponent = fieldsToRender.some(
        field => field.name === 'cardNum' &&
                (field.renderComponent === 'CardInformationForm' ||
                 field.componentType === 'card-information' ||
                 field.renderComponent === 'card-information'),
      );

      if (hasCardInfoComponent) {
        // 过滤掉effectiveDate和cvv字段，因为它们已经包含在CardInformationForm中
        return fieldsToRender.filter(field =>
          !(field.name === 'effectiveDate' || field.name === 'cvv'),
        );
      }

      return fieldsToRender;
    },

    // 当前选择的国家
    selectedCountry() {
      const countryField = this.renderFields.find(field => field.name === 'country');
      return countryField ? this.formData[countryField.name] : '';
    },

    // 是否正在进行完整验证（包括必填）
    isFullValidation() {
      return this.internalValidationMode === 'full';
    },
  },

  created() {
    // 先格式化选项，再设置初始值
    this.formatStateOptions();
    // 初始化验证器
    this.initValidators();
    // 最后初始化表单数据
    this.initFormData();
  },

  methods: {
    // 判断是否显示标签
    showLabel(field) {
      // 如果有标签文本就显示标签
      return !!this.getLabel(field);
    },

    // 获取标签文本
    getLabel(field) {
      // 如果字段有明确指定label，优先使用
      if (field.label) {
        // 处理多语言键
        if (typeof field.label === 'string' && field.label.startsWith('message.')) {
          return this.$t(field.label);
        }
        return field.label;
      }

      return field.name;
    },

    // 初始化字段验证器
    initValidators() {
      this.validators = {};
      this.config.forEach(field => {
        // 检查字段是否有验证规则
        if (field.validationRules && field.validationRules.length) {
          // 为每个字段创建专用的验证器实例
          this.validators[field.name] = createValidator(this.$i18n);
        }
      });
    },

    // 格式化州/省选项为 label/value 结构
    formatStateOptions() {
      const formattedOptions = {};
      for (const country in STATES_OPTIONS) {
        formattedOptions[country] = STATES_OPTIONS[country].map(state => ({
          label: this.$t(`message.state.${country}.${state.value}`) || state.label,
          value: state.value,
        }));
      }
      this.stateOptions = formattedOptions;
    },

    // 初始化表单数据，直接使用原始值
    initFormData() {
      // 创建全新的空对象作为表单数据
      this.formData = {};

      // 只设置配置中定义的字段
      this.config.forEach(field => {
        const initialValue = this.initialValues[field.name] !== undefined
          ? this.initialValues[field.name]
          : field.value;

        this.$set(this.formData, field.name, initialValue || '');

        // 分期字段
        if (field.name === 'periodValue' && field.options.length) {
          this.$set(this.formData, field.name, field.options[0].value);
        }
      });
    },

    // 获取字段有效的验证规则（根据当前验证模式）
    getFieldRules(field) {
      // 获取原始规则
      const rules = field.validationRules ? [...field.validationRules] : [];

      // 如果是完整验证模式且字段是必填的，确保包含required规则
      if (this.internalValidationMode === 'full' && field.require === 'Y' &&
          !rules.some(rule => rule === 'required' || (typeof rule === 'object' && rule.name === 'required'))) {
        rules.unshift('required');
      }

      // 如果是仅格式验证模式，过滤掉所有required规则
      if (this.internalValidationMode === 'format-only') {
        return rules.filter(rule => {
          if (typeof rule === 'string') {
            return rule !== 'required';
          } else {
            return rule.name !== 'required';
          }
        });
      }

      return rules;
    },

    // 验证单个字段
    validateField(field) {
      const fieldName = field.name;
      const value = this.formData[fieldName];

      // 处理卡信息组件的特殊情况 - 让卡信息组件内部自己处理验证和错误展示
      if (field.componentType === 'card-information' || field.renderComponent === 'CardInformationForm') {
        const fieldRef = this.$refs[`field-${fieldName}`];

        if (fieldRef && fieldRef[0]?.$children[0]?.validate) {
          // 调用卡信息组件的validate方法
          const isValid = fieldRef[0]?.$children[0]?.validate();

          return { valid: isValid };
        }
        return { valid: true };
      }

      // 常规组件验证
      const validator = this.validators[fieldName];
      if (!validator) return { valid: true };

      const rules = this.getFieldRules(field);
      if (!rules.length) {
        this.$delete(this.errors, fieldName);
        return { valid: true };
      }

      const result = validator.validate(value, rules);
      if (!result.valid) {
        this.$set(this.errors, fieldName, result.error);
        return result;
      }

      this.$delete(this.errors, fieldName);
      return { valid: true };
    },

    // 表单校验
    validate() {
      this.errors = {};
      let isValid = true;

      // 遍历需要渲染的字段进行校验
      for (const field of this.renderFields) {
        // 验证字段
        const result = this.validateField(field);

        if (!result.valid) {
          isValid = false;
        }

        // 如果是行字段，还需要验证其所有子字段
        if (field.isRowFields && field.rowFields && field.rowFields.length) {
          for (const subfield of field.rowFields) {
            // 验证子字段
            if (subfield.require === 'Y' && this.internalValidationMode === 'full' && !this.formData[subfield.name]) {
              // 必填字段但值为空
              this.$set(this.errors, subfield.name, this.$t('message.validator.required'));
              isValid = false;
            } else if (subfield.validationRules && subfield.validationRules.length) {
              // 有验证规则，执行验证
              const validator = this.validators[subfield.name];
              if (validator) {
                const rules = this.getFieldRules(subfield);
                if (rules.length) {
                  const result = validator.validate(this.formData[subfield.name], rules);
                  if (!result.valid) {
                    this.$set(this.errors, subfield.name, result.error);
                    isValid = false;
                  }
                }
              }
            }
          }
        }
      }

      // 触发校验完成事件，传递校验结果和表单数据
      this.$emit('validate', {
        valid: isValid,
        data: this.formData,
        errors: this.errors,
      });

      return isValid;
    },

    // 设置验证模式
    setValidationMode(mode) {
      if (['format-only', 'full'].includes(mode)) {
        this.internalValidationMode = mode;
        // 切换到完整验证模式时，始终重新验证表单以更新所有字段的错误状态
        if (mode === 'full') {
          this.$nextTick(() => {
            this.validate();
          });
        } else if (Object.keys(this.errors).length > 0) {
          // 否则只在已有错误时重新验证
          this.validate();
        }
        return true;
      }
      return false;
    },

    // 处理表单提交 - 支持异步校验
    async handleSubmit() {
      // 防止重复提交
      if (this.submitting) {
        return Promise.resolve({ valid: false, reason: 'submitting' });
      }

      // 设置提交状态
      this.submitting = true;

      try {
        // 切换到完整验证模式（包括必填验证）
        this.setValidationMode('full');

        // 执行表单验证
        const isValid = this.validate();

        // 如果验证失败，触发错误事件
        if (!isValid) {
          this.$emit('submit-error', this.errors);

          // 滚动到第一个错误字段
          await this.scrollToFirstError();

          return {
            valid: false,
            errors: this.errors,
          };
        }

        // 验证通过，触发提交事件
        this.$emit('submit', this.formData);

        return {
          valid: true,
          data: this.formData,
        };
      } catch (error) {
        // 触发提交异常事件
        this.$emit('submit-exception', error);
        return {
          valid: false,
          error,
        };
      } finally {
        // 恢复提交状态
        this.submitting = false;
      }
    },

    // 滚动到第一个错误字段
    scrollToFirstError() {
      return new Promise(resolve => {
        this.$nextTick(() => {
          // 先检查是否有卡信息组件的错误
          const cardField = this.renderFields.find(field =>
            field.componentType === 'card-information' ||
            field.renderComponent === 'CardInformationForm',
          );

          if (cardField) {
            const cardRef = this.$refs[`field-${cardField.name}`];
            if (cardRef && cardRef[0]?.$children[0]?.validate && !cardRef[0]?.$children[0]?.validate()) {
              // 如果卡信息组件验证失败，滚动到卡信息组件
              const fieldWrapper = document.getElementById(`field-${cardField.name}`);
              if (fieldWrapper) {
                fieldWrapper.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'nearest',
                });
                resolve(true);
                return;
              }
            }
          }

          // 如果没有卡信息组件错误，或者卡信息组件验证通过，检查其他字段错误
          const firstErrorField = Object.keys(this.errors)[0];
          if (!firstErrorField) {
            resolve(false);
            return;
          }

          // 使用ID选择器查找字段包装器
          const fieldWrapper = document.getElementById(`field-${firstErrorField}`);

          if (fieldWrapper) {
            // 使用更平滑的滚动效果
            fieldWrapper.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });

            resolve(true);
          } else {
            resolve(false);
          }
        });
      });
    },

    // 提供公开方法用于外部调用表单提交
    submit() {
      return this.handleSubmit();
    },

    // 已废弃的方法，保留以向后兼容
    setInstallmentFieldDisabled(disabled) {
      console.warn('setInstallmentFieldDisabled已废弃，分期字段状态应由外部组件管理');
    },

    // 验证卡号（使用Luhn算法）
    validateCardNumber(cardNumber) {
      if (!cardNumber || cardNumber.length < 10) return false;

      // 去除空格
      const cardNum = cardNumber.replace(/\s+/g, '');

      // 检查是否全是数字
      if (!/^\d+$/.test(cardNum)) return false;

      // Luhn算法校验
      let sum = 0;
      let alternate = false;

      for (let i = cardNum.length - 1; i >= 0; i--) {
        let n = parseInt(cardNum.substring(i, i + 1));
        if (alternate) {
          n *= 2;
          if (n > 9) {
            n = (n % 10) + 1;
          }
        }
        sum += n;
        alternate = !alternate;
      }

      // 卡号验证结果
      const isValid = (sum % 10 === 0);

      // 触发卡号验证结果事件
      if (isValid) {
        this.$emit('card-valid', cardNum);
      }

      return isValid;
    },

    // 获取表单数据
    getFormData() {
      return this.formData;
    },

    // 设置表单数据
    setFormData(data) {
      Object.keys(data).forEach(key => {
        this.$set(this.formData, key, data[key]);
      });
    },

    // 重置表单数据（保留指定值）
    resetFormData(data = {}) {
      // 清空所有现有的表单数据，但保留指定的值
      this.formData = { ...data };
      this.$emit('form-data-reset', this.formData);
    },

    // 获取当前字段配置
    getCurrentFieldConfig() {
      return this.config;
    },

    // 更新字段配置（主要用于更新选项等）
    updateFieldConfig() {
      // 触发更新事件
      this.$emit('field-config-update', this.config);
      // 强制组件重新渲染
      this.$forceUpdate();
    },

    // 获取表单错误
    getErrors() {
      return this.errors;
    },

    // 重置表单
    resetForm() {
      // 清空所有验证状态
      this.errors = {};
      this.cardInfoRequiredError = false;

      // 重新设置为格式验证模式
      this.internalValidationMode = 'format-only';

      // 完全重置表单数据为空对象
      this.formData = {};

      // 先格式化选项，再设置初始值
      this.formatStateOptions();
      // 初始化验证器
      this.initValidators();
      // 最后初始化表单数据
      this.initFormData();

      this.$emit('reset');
    },

    // 字段聚焦事件处理
    onFieldFocus(data) {
      this.$emit('field-focus', data);
    },

    // 字段失焦事件处理
    onFieldBlur(data) {
      // 找到字段配置
      const field = this.renderFields.find(f => f.name === data.name);

      // 仅在失焦时验证非卡信息组件字段
      if (field && !this.isCardInfoComponent(field)) {
        // 在完整验证模式下始终验证，在格式验证模式下只验证非空字段
        if (this.internalValidationMode === 'full' ||
            (this.formData[field.name] && this.formData[field.name].length > 0)) {
          this.validateField(field);
        }
      }

      this.$emit('field-blur', {
        ...data,
        error: !!this.errors[data.name],
      });
    },

    // 判断是否为卡信息组件
    isCardInfoComponent(field) {
      return field.renderComponent === 'CardInformationForm' ||
             field.renderComponent === 'card-information' ||
             field.componentType === 'card-information';
    },

    // 字段值变更事件处理
    onFieldChange(data) {
      this.$emit('field-change', data);

      // 如果变更的是国家字段，需要处理省/州字段的组件类型
      if (data.name === 'country') {
        // 只有当国家类型发生变化时才需要重置province
        const oldCountryNeedsSelect = this.provinceSelectCountries.includes(this.formData.country);
        const newCountryNeedsSelect = this.provinceSelectCountries.includes(data.value);

        // 只有当组件类型变化时才清空值（从select到input或从input到select）
        if (oldCountryNeedsSelect !== newCountryNeedsSelect) {
          this.formData.province = '';
        }
      }

      // 如果变更的是卡号字段，仅验证卡号并触发事件
      if (data.name === 'cardNum') {
        this.validateCardNumber(data.value);
      }
    },

    // 更新分期字段选项 - 空方法保留以向后兼容
    updatePeriodValueOptions(options) {
      console.warn('updatePeriodValueOptions已废弃，分期选项应由外部组件管理');
    },

    // 获取字段组件类型
    getComponentType(field) {
      // 特殊处理省/州字段
      if (field.name === 'province') {
        // 如果当前国家是US/CA/CN，使用下拉框，否则使用输入框
        if (this.selectedCountry && this.provinceSelectCountries.includes(this.selectedCountry)) {
          return 'BaseSelect';
        }
        return 'BaseInput';
      }

      // 特殊处理分期字段
      if (field.name === 'periodValue') {
        return 'BaseSelect';
      }

      return field.componentType;
    },

    // 获取字段配置，主要用于动态处理省/州字段和分期字段
    getFieldConfig(field) {
      // 复制一个新字段配置，避免修改原始配置
      const newField = {
        ...field,
        // 确保验证规则被正确传递
        validationRules: field.validationRules ? [...field.validationRules] : [],
        // 添加字段名称属性，用于错误定位
        dataField: field.name,
      };

      // 特殊处理省/州字段
      if (field.name === 'province') {
        if (this.selectedCountry && this.provinceSelectCountries.includes(this.selectedCountry)) {
          // 准备选项列表
          const options = this.stateOptions[this.selectedCountry] || [];
          newField.options = options;
          newField.renderComponent = 'BaseSelect';

          // 检查当前值是否在选项列表中，如果不在则默认选择第一个选项
          if (this.formData.province) {
            const isValidOption = options.some(option => option.value === this.formData.province);
            if (!isValidOption && options.length > 0) {
              // 延迟设置值，避免与当前渲染周期冲突
              this.$nextTick(() => {
                this.formData.province = options[0].value;
              });
            }
          } else if (options.length > 0) {
            // 如果没有值，默认选择第一个选项
            this.$nextTick(() => {
              this.formData.province = options[0].value;
            });
          }
        } else {
          newField.renderComponent = 'BaseInput';
        }
      }

      // 特殊处理分期字段
      if (field.name === 'periodValue') {
        newField.renderComponent = 'BaseSelect';
        // 不再在组件内部控制分期字段状态
        newField.isInstallmentField = true;
      }

      return newField;
    },

    // 处理行字段输入事件
    onRowFieldInput(data) {
      if (data && data.name) {
        this.$set(this.formData, data.name, data.value);
      }
    },

    // 处理组件验证事件
    onFieldValidate(data) {
      if (!data || !data.name) return;

      // 卡信息组件的特殊处理 - 只处理必填错误标记
      if (data.isCardInfo) {
        if (data.allFieldsRequired !== undefined) {
          this.cardInfoRequiredError = data.allFieldsRequired;
        }
        return;
      }

      // 处理常规组件验证结果
      if (data.valid === true) {
        this.$delete(this.errors, data.name);
      } else if (data.valid === false && data.errors) {
        Object.keys(data.errors).forEach(errorField => {
          this.$set(this.errors, errorField, data.errors[errorField]);
        });
      }
    },
  },

  watch: {
    // 监听验证模式变更
    validationMode: {
      handler(newMode) {
        // 更新内部验证模式
        this.internalValidationMode = newMode;
        // 重新验证表单以更新错误状态
        if (Object.keys(this.errors).length > 0) {
          this.validate();
        }
      },
      immediate: true,
    },

    // 监听config变化，确保完全重新初始化表单
    config: {
      handler(newConfig, oldConfig) {
        // 保存当前表单数据
        const currentFormData = { ...this.formData };

        // 执行原有的重置逻辑
        this.errors = {};
        this.cardInfoRequiredError = false;
        this.internalValidationMode = 'format-only';
        this.formData = {};
        this.formatStateOptions();
        this.initValidators();
        this.initFormData();

        // 还原之前的表单数据
        Object.keys(currentFormData).forEach(key => {
          if (key !== 'periodValue') {
            this.$set(this.formData, key, currentFormData[key]);
          }
        });
      },
      deep: true,
    },

    // 监听国家变化，确保省份值有效
    selectedCountry(newValue, oldValue) {
      if (newValue !== oldValue) {
        const newCountryNeedsSelect = this.provinceSelectCountries.includes(newValue);
        const oldCountryNeedsSelect = this.provinceSelectCountries.includes(oldValue);

        // 只有当组件类型变化时才需要处理
        if (newCountryNeedsSelect !== oldCountryNeedsSelect) {
          if (newCountryNeedsSelect) {
            // 从input变为select，确保有有效的选项值
            const options = this.stateOptions[newValue] || [];
            if (options.length > 0) {
              this.formData.province = options[0].value;
            } else {
              this.formData.province = '';
            }
          } else {
            // 从select变为input，清空值
            this.formData.province = '';
          }
        } else if (newCountryNeedsSelect) {
          // 如果仍然是select，但国家变了，检查当前值是否有效
          const options = this.stateOptions[newValue] || [];
          const isValidOption = options.some(option => option.value === this.formData.province);

          if (!isValidOption && options.length > 0) {
            this.formData.province = options[0].value;
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.dynamic-form {
  margin: 0 auto;
  width: 100%;

  .form-field-wrapper {
    position: relative;
    margin-bottom: 24px;
    width: 100%;
    transition: all 0.3s ease;
    border-radius: 8px;
  }

  .highlight-error {
    border-radius: 8px;
  }

  .form-field-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
  }

  .field-label {
    display: flex;
    align-items: center;
    font-size: 14px;

    .label-text {
      color: #1A202C;
      font-weight: 500;
      font-family: "Open Sans regular";
    }
  }

  .field-error {
    font-size: 12px;
    color: $color-form-error;
    font-family: Open Sans regular;
    &.top-right {
      text-align: right;
    }

    &.bottom-left {
      display: block;
      margin-top: 6px;
      margin-bottom: 0;
      min-height: 0;
      text-align: left;
    }

    &.row-field-error {
      flex: 1;
      display: block;
      margin-top: 6px;
      min-height: 18px;
      text-align: left;
    }

    &.card-component-error {
      margin-top: 2px;
      margin-bottom: 8px;
      color: #f56c6c;
      font-weight: 400;
    }
  }

  .row-fields-errors {
    display: flex;
    gap: 12px;
    width: 100%;
    margin-bottom: 24px;
  }

  // Vue Transition动画
  .slide-top-enter-active,
  .slide-bottom-enter-active {
    animation: slideDown 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  }

  .slide-top-leave-active,
  .slide-bottom-leave-active {
    animation: slideUp 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  }

  // 滑入动画 - 从上往下
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-15px);
      max-height: 0;
    }
    to {
      opacity: 1;
      transform: translateY(0);
      max-height: 50px;
    }
  }

  // 滑出动画 - 从下往上
  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0);
      max-height: 50px;
    }
    to {
      opacity: 0;
      transform: translateY(-15px);
      max-height: 0;
    }
  }

  &__actions {
    margin-top: 24px;
    display: flex;
    justify-content: center;
  }
}
</style>
